import {
  useGetUserConfig,
  useUpdateUserConfig,
} from "@/services/api/userConfigService"
import type {
  MaterialComplianceUserConfig,
  McTableUserConfig,
  UserConfigDocument,
} from "@/types/userConfig"
import { debounce } from "@/utils/optimisationUtils"
import { useEffect, useMemo, useRef, useState } from "react"

const USER_CONFIG_SAVE_DELAY_MS = 500

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

/** Parses persisted column widths defensively — any malformed entry is dropped. */
function getPersistedColumnWidths(
  config: McTableUserConfig | undefined,
): Record<string, number> {
  if (!isRecord(config?.columnWidthsById)) return {}
  return Object.entries(config.columnWidthsById).reduce<Record<string, number>>(
    (widths, [columnId, width]) => {
      if (typeof width === "number" && Number.isFinite(width) && width > 0) {
        widths[columnId] = width
      }
      return widths
    },
    {},
  )
}

/**
 * Persists an McTable's column widths under `configuration.materialCompliance.<tableKey>`
 * via the User Preference API — hydrates once from the fetched config, then
 * debounces writes back on every change. Pass the result straight into McTable's
 * `columnWidths` / `onColumnWidthsChange` props.
 *
 * `defaultColumnWidths` seeds columns the user hasn't manually resized yet —
 * a persisted (user-dragged) width always wins over it.
 */
export function useMcTableColumnWidths(
  tableKey: keyof MaterialComplianceUserConfig,
  defaultColumnWidths?: Record<string, number>,
) {
  const { data: userConfigPayload, isPending: isUserConfigPending } =
    useGetUserConfig()
  const { mutate: updateUserConfig } = useUpdateUserConfig()
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const hasHydratedColumnWidthsRef = useRef(false)

  const configuration = userConfigPayload?.configuration
  const persistedColumnWidths = useMemo(
    () =>
      getPersistedColumnWidths(configuration?.materialCompliance?.[tableKey]),
    [configuration, tableKey],
  )

  useEffect(() => {
    if (hasHydratedColumnWidthsRef.current || isUserConfigPending) return
    setColumnWidths({ ...defaultColumnWidths, ...persistedColumnWidths })
    hasHydratedColumnWidthsRef.current = true
    // Only the initial hydration should apply defaults/persisted state —
    // `defaultColumnWidths` is intentionally excluded so a caller passing a
    // fresh object literal each render doesn't re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserConfigPending, persistedColumnWidths])

  const persistColumnWidths = useMemo(
    () =>
      debounce(
        (
          widths: Record<string, number>,
          baseConfiguration: UserConfigDocument,
        ) => {
          updateUserConfig({
            configuration: {
              ...baseConfiguration,
              materialCompliance: {
                ...(isRecord(baseConfiguration.materialCompliance)
                  ? baseConfiguration.materialCompliance
                  : {}),
                [tableKey]: { columnWidthsById: widths },
              },
            },
          })
        },
        USER_CONFIG_SAVE_DELAY_MS,
      ),
    [updateUserConfig, tableKey],
  )

  useEffect(() => {
    if (!hasHydratedColumnWidthsRef.current || isUserConfigPending) return
    if (
      JSON.stringify(columnWidths) === JSON.stringify(persistedColumnWidths)
    ) {
      return
    }
    persistColumnWidths(columnWidths, configuration ?? {})
  }, [
    columnWidths,
    configuration,
    isUserConfigPending,
    persistColumnWidths,
    persistedColumnWidths,
  ])

  return [columnWidths, setColumnWidths] as const
}
